import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Bot, Key, Shield, Zap, Database, MessageSquare, FileText, Package, Info } from 'lucide-react';
import toast from 'react-hot-toast';

const Login = () => {
  const [apiKey, setApiKey] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!apiKey.trim()) {
      toast.error('Please enter your API key');
      return;
    }

    setIsLoading(true);
    
    try {
      await login(apiKey.trim(), {
        redirectTo: '/dashboard',
      });
    } catch (error) {
      console.error('Login failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: FileText,
      title: 'Document Processing',
      description: 'Upload and index PDF, Word, Excel, and text documents with intelligent content extraction.'
    },
    {
      icon: Bot,
      title: 'AI-Powered Queries',
      description: 'Ask questions in natural language and get intelligent answers from your documents using OpenAI or Claude.'
    },
    {
      icon: Package,
      title: 'Product Mapping',
      description: 'Organize documents by products and filter queries for targeted information retrieval.'
    },
    {
      icon: Database,
      title: 'Multi-tenant Architecture',
      description: 'Secure, isolated data storage with Row-Level Security for enterprise-grade applications.'
    },
    {
      icon: MessageSquare,
      title: 'Chat Interface',
      description: 'Intuitive chat interface for document queries with conversation history and context.'
    },
    {
      icon: Shield,
      title: 'Secure & Scalable',
      description: 'Built with security best practices, rate limiting, and scalable architecture.'
    }
  ];

  return (
    <div className="min-vh-100 d-flex align-items-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="row g-0 shadow-lg rounded-4 overflow-hidden">
              {/* Left side - Features */}
              <div className="col-lg-7 bg-white p-5 d-none d-lg-flex flex-column justify-content-center">
                <div className="text-center mb-5">
                  <div className="mb-3">
                    <img 
                      src="/logo.png" 
                      alt="AI Tech Assistant Logo" 
                      style={{ height: '60px', width: 'auto' }}
                      className="d-inline-block"
                    />
                  </div>
                  <h2 className="h3 text-primary fw-bold mb-2">AI Tech Assistant</h2>
                  <p className="text-muted mb-0">Intelligent Document Indexing & Retrieval System</p>
                </div>

                <div className="row g-4">
                  {features.map((feature, index) => {
                    const Icon = feature.icon;
                    return (
                      <div key={index} className="col-md-6">
                        <div className="d-flex align-items-start gap-3">
                          <div className="bg-light rounded-circle p-2 flex-shrink-0">
                            <Icon size={20} className="text-primary" />
                          </div>
                          <div>
                            <h6 className="fw-semibold mb-1">{feature.title}</h6>
                            <p className="small text-muted mb-0">{feature.description}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="text-center mt-5">
                  <div className="d-flex align-items-center justify-content-center gap-2 text-muted">
                    <Shield size={16} />
                    <span className="small">Enterprise-grade security & compliance</span>
                  </div>
                </div>
              </div>

              {/* Right side - Login Form */}
              <div className="col-lg-5 bg-light p-5 d-flex flex-column justify-content-center">
                <div className="text-center mb-4">
                  <div className="mb-3 d-lg-none">
                    <img 
                      src="/logo.png" 
                      alt="AI Tech Assistant Logo" 
                      style={{ height: '40px', width: 'auto' }}
                      className="d-inline-block"
                    />
                  </div>
                  <h3 className="fw-bold mb-2">Welcome Back</h3>
                  <p className="text-muted">Enter your API key to access the system</p>
                </div>

                <form onSubmit={handleSubmit} className="needs-validation" noValidate>
                  <div className="mb-4">
                    <label htmlFor="apiKey" className="form-label fw-semibold">
                      API Key
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-white border-end-0">
                        <Key size={18} className="text-muted" />
                      </span>
                      <input
                        type="password"
                        className="form-control border-start-0"
                        id="apiKey"
                        placeholder="Enter your API key"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        required
                        disabled={isLoading}
                      />
                    </div>
                    <div className="form-text">
                      Your API key provides secure access to the system
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary w-100 py-2 mb-3"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Authenticating...
                      </>
                    ) : (
                      <>
                        <Zap size={18} className="me-2" />
                        Access System
                      </>
                    )}
                  </button>

                  <div className="text-center">
                    <small className="text-muted">
                      Don't have an API key? Contact your administrator
                    </small>
                  </div>
                </form>

                {/* Quick Info */}
                <div className="mt-5">
                  <div className="card border-0 bg-primary-subtle">
                    <div className="card-body p-3">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <Info size={16} className="text-primary" />
                        <span className="small fw-semibold text-primary">Getting Started</span>
                      </div>
                      <p className="small text-muted mb-0">
                        After login, you can upload documents, configure AI providers, and start querying your knowledge base.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .bg-primary-subtle {
          background-color: rgba(13, 110, 253, 0.1) !important;
        }
        
        .input-group .form-control:focus {
          border-color: #0d6efd;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }
        
        .input-group .form-control:focus + .input-group-text {
          border-color: #0d6efd;
        }
        
        .btn-primary {
          background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
          border: none;
          transition: all 0.3s ease;
        }
        
        .btn-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
        }
        
        .card {
          transition: all 0.3s ease;
        }
        
        .card:hover {
          transform: translateY(-2px);
        }
        
        @media (max-width: 991.98px) {
          .col-lg-7 {
            display: none !important;
          }
          
          .col-lg-5 {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default Login;
